/**
 * Transaction Attributes API Service
 *
 * Service for fetching transaction attributes from backend with i18n support
 * Replaces hardcoded constants with dynamic backend data
 */

import type {
  GetTransactionAttributesRequest,
  GetTransactionAttributesResponse,
  TransactionAttributeResponse,
} from '@bat/shared'

import { apiService } from '@/platform/api/api.service'
import { ApiException } from '@/shared/utils/api'
import { logger } from '@/shared/utils/logger'

/**
 * Cache interface for attributes data.
 *
 * `signature` is the FULL request identity (locale + category + activeOnly), not
 * just the locale. Two different questions share this endpoint — "what may the
 * user choose?" (activeOnly=true) and "what label does this saved key render?"
 * (activeOnly=false) — and a locale-only key served one answer to the other. The
 * dangerous direction is full → offerable: the picker would be handed a
 * DEACTIVATED attribute straight out of the cache. A category-scoped response
 * likewise used to be stored under the unscoped key and returned to the next
 * unscoped caller as if it were the whole catalog.
 */
interface AttributesCache {
  data: GetTransactionAttributesResponse | null
  timestamp: number
  signature: string
}

/** Stable identity of a getAll request — every param that changes the response. */
function buildAttributesCacheSignature(
  locale: string,
  category: string | undefined,
  activeOnly: boolean | undefined,
): string {
  return `${locale}|${category ?? ''}|${activeOnly === undefined ? 'default' : String(activeOnly)}`
}

/**
 * Transaction Attributes API Service
 */
class TransactionAttributesApiService {
  private cache: AttributesCache = {
    data: null,
    timestamp: 0,
    signature: '',
  }

  private readonly CACHE_TTL = 5 * 60 * 1000 // 5 minutes

  /**
   * Check if cache is valid for THIS exact request identity.
   */
  private isCacheValid(signature: string): boolean {
    if (!this.cache.data || this.cache.signature !== signature) {
      return false
    }

    return Date.now() - this.cache.timestamp < this.CACHE_TTL
  }

  /**
   * Get all transaction attributes from backend
   */
  async getAll(
    options?: GetTransactionAttributesRequest,
  ): Promise<GetTransactionAttributesResponse> {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- '' locale must fall back to 'en'
    const locale = options?.locale || 'en'
    const signature = buildAttributesCacheSignature(locale, options?.category, options?.activeOnly)

    // Check cache — same locale AND same category AND same activeOnly.
    if (this.isCacheValid(signature) && this.cache.data) {
      return this.cache.data
    }

    try {
      const apiResponse = await apiService.get<GetTransactionAttributesResponse>(
        '/api/transaction-attributes',
        {
          params: {
            locale,
            category: options?.category,
            activeOnly: options?.activeOnly,
          },
        },
      )

      // apiResponse.data already contains { attributes, categories, locale, ... }
      // BUT can be null when the request was aborted mid-flight (e.g. user
      // navigates away from /transactions before the response lands) — the
      // earlier `response.attributes` access then threw a real TypeError
      // ("Cannot read properties of null (reading 'attributes')") that
      // showed up as a noisy red console error on every page render that
      // mounted a useTransactionAttributes consumer. Guard against null
      // first.
      const response = apiResponse.data

      if (!response?.attributes || !Array.isArray(response.attributes)) {
        throw new ApiException({
          status: 0,
          code: 'TRANSACTION_ATTRIBUTES_INVALID_RESPONSE',
          message: '',
        })
      }

      // Update cache under this request's exact identity.
      this.cache = {
        data: response,
        timestamp: Date.now(),
        signature,
      }

      return response
    } catch (error) {
      // Demote mid-flight aborts / network blips / no-data-returned
      // variants to a debug log: these are benign on every navigation
      // away from a page that mounted a useTransactionAttributes
      // consumer. Keep error-level logging for everything else (real
      // 4xx/5xx).
      const code =
        (error as { code?: string } | null)?.code ??
        (error as { error?: { code?: string } } | null)?.error?.code
      const benignCodes = new Set([
        'TRANSACTION_ATTRIBUTES_INVALID_RESPONSE',
        'TIMEOUT',
        'NETWORK_ERROR',
        'REQUEST_ABORTED',
      ])
      if (benignCodes.has(code ?? '')) {
        logger.debug('Transaction attributes request aborted/timed out:', error)
      } else {
        logger.error('Error fetching transaction attributes:', error)
      }
      throw error
    }
  }

  /**
   * Get attributes grouped by category for modal display
   */
  async getAttributesGrouped(
    locale?: string,
  ): Promise<Record<string, TransactionAttributeResponse[]>> {
    const data = await this.getAll({ locale, activeOnly: true })

    const grouped: Record<string, TransactionAttributeResponse[]> = {}

    data.attributes.forEach((attr) => {
      if (!grouped[attr.category]) {
        grouped[attr.category] = []
      }
      grouped[attr.category].push(attr)
    })

    // Sort attributes within each category by sortOrder
    Object.keys(grouped).forEach((category) => {
      grouped[category].sort((a, b) => a.sortOrder - b.sortOrder)
    })

    return grouped
  }

  /**
   * Get attribute by key with translations
   */
  async getByKey(key: string, locale?: string): Promise<TransactionAttributeResponse | null> {
    const data = await this.getAll({ locale })
    return data.attributes.find((attr) => attr.key === key) ?? null
  }

  /**
   * Get all unique categories
   */
  async getCategories(locale?: string): Promise<string[]> {
    const data = await this.getAll({ locale })
    return data.categories
  }

  /**
   * Clear cache (useful for language changes)
   */
  clearCache(): void {
    this.cache = {
      data: null,
      timestamp: 0,
      signature: '',
    }
  }

  /**
   * Get translated name for an attribute key
   */
  async getTranslatedName(key: string, locale?: string): Promise<string> {
    const attribute = await this.getByKey(key, locale)
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- '' name must fall back to the key
    return attribute?.name || key
  }

  /**
   * Get attributes for a specific category
   */
  async getByCategory(category: string, locale?: string): Promise<TransactionAttributeResponse[]> {
    const data = await this.getAll({ locale, category, activeOnly: true })
    return data.attributes.filter((attr) => attr.category === category)
  }

  /**
   * Batch get translated names for multiple keys
   */
  async getTranslatedNames(keys: string[], locale?: string): Promise<Record<string, string>> {
    const data = await this.getAll({ locale })
    const nameMap: Record<string, string> = {}

    keys.forEach((key) => {
      const attribute = data.attributes.find((attr) => attr.key === key)
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- '' name must fall back to the key
      nameMap[key] = attribute?.name || key
    })

    return nameMap
  }
}

// Export singleton instance
export const transactionAttributesApiService = new TransactionAttributesApiService()
